import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, CheckCircle2, Clock, Mail, Search, Sparkles, Tag } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getPosts } from "../lib/blog-api";
import Header from "./Header";
import Footer from "./Footer";
// import type { WPPost } from "@/lib/blog-types";
// type BlogCategory = string;

// type BlogSearch = {
//   category?: BlogCategory | "All";
//   q?: string;
// };

const SITE = "https://brightpath-hero.lovable.app";


const ease = [0.16, 1, 0.3, 1];

function BlogIndex() {
  const [query, setQuery] = useState("");
  const [activeCat, setCategory] = useState("All");
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    setLoading(true);
    getPosts(page)
      .then((data) => {
        console.log("Page =", page);
        console.log("Received =", data.length);
        if (data.length < 9) {
          setHasMore(false);
        }
        setPosts(prev => {
          const merged = [...prev, ...data];
          return merged.filter(
            (post, index, self) =>
              index === self.findIndex(p => p.id === post.id)
          );
        });
      })
      .finally(() => setLoading(false));
  }, [page]);

  const categories = useMemo(() => {
    const cats = posts.flatMap(
      p => p._embedded?.["wp:term"]?.[0]?.map(c => c.name) || []
    );

    return [...new Set(cats.filter(Boolean))];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const q = query.toLowerCase();
    return posts.filter(post => {
      const title = post.title.rendered.toLowerCase();
      const excerpt = post.excerpt.rendered.replace(/<[^>]+>/g, "").toLowerCase();
      const content = post.content.rendered.replace(/<[^>]+>/g, "").toLowerCase();
      const category = post._embedded?.["wp:term"]?.[0]?.[0]?.name?.toLowerCase() || "";
      const searchMatch = title.includes(q) || excerpt.includes(q) || content.includes(q) || category.includes(q);
      const postCategories = post._embedded?.["wp:term"]?.[0]?.map(c => c.name) || [];
      const categoryMatch = activeCat === "All" || postCategories.includes(activeCat); return searchMatch && categoryMatch;
    });

  }, [posts, query, activeCat]);
  const featured = filteredPosts[0];
  const rest = filteredPosts.slice(1);


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pb-24">
        {/* Hero + Search + Filters */}
        <BlogHero
          query={query}
          setQuery={setQuery}
          activeCat={activeCat}
          setCategory={setCategory}
          categories={categories}
        />

        {/* Featured */}
        {featured && (
          <section className="container mx-auto mt-14 max-w-6xl px-6">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease }}
            >
              <Link
                to="/blog/$slug"
                params={{ slug: featured.slug }}
                className="group block overflow-hidden rounded-3xl border border-border bg-surface-elevated shadow-soft transition duration-500 hover:-translate-y-1 hover:shadow-elevated"
              >
                <div className="grid gap-0 md:grid-cols-2">
                  {(() => {
                    const v = getCategoryVisual("General");
                    return (
                      <div
                        className={`relative aspect-[4/3] overflow-hidden bg-gradient-to-br ${v.gradient} md:aspect-auto`}
                      >
                        <div
                          aria-hidden
                          className="absolute inset-0 opacity-[0.18]"
                          style={{
                            backgroundImage:
                              "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.6) 1px, transparent 0)",
                            backgroundSize: "22px 22px",
                          }}
                        />
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full blur-3xl"
                          style={{ background: v.accent, opacity: 0.45 }}
                        />
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-white/40 blur-3xl"
                        />
                        <div className="absolute inset-0 grid place-items-center text-[120px] transition-transform duration-700 group-hover:scale-110 md:text-[160px]">
                          <img
                            src={featured._embedded?.["wp:featuredmedia"]?.[0]?.source_url}
                            className="h-full w-full object-cover object-center"
                            alt={featured.title.rendered}
                          />
                        </div>
                        <div className="absolute left-5 top-5 inline-flex items-center gap-1 rounded-full bg-background/90 px-3 py-1 text-[11px] font-medium text-ink backdrop-blur">
                          <Sparkles className="h-3 w-3 text-brand" />
                          Featured · {featured._embedded?.["wp:term"]?.[0]?.[0]?.name}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex flex-col justify-between p-8 md:p-10">
                    <div>
                      <div className="flex items-center gap-3 text-[12px] text-ink-soft">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(featured.date)}
                        </span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {getReadMinutes(featured.content.rendered)} min read
                        </span>
                      </div>
                      <h2 className="mt-4 font-display text-3xl leading-tight tracking-tight text-ink md:text-4xl">
                        {featured.title.rendered}
                      </h2>
                      <div
                        className="mt-4 text-[15px] leading-relaxed text-ink-soft"
                        dangerouslySetInnerHTML={{
                          __html: featured.excerpt.rendered
                        }}
                      />
                    </div>
                    <div className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-ink">
                      Read article
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </section>
        )}

        {/* Grid */}
        <section className="container mx-auto mt-14 max-w-6xl px-6">
          {loading && posts.length === 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-2xl border border-border bg-surface-elevated animate-pulse"
                >
                  <div className="aspect-[16/10] bg-slate-200" />

                  <div className="p-6">
                    <div className="h-3 w-32 rounded bg-slate-200" />

                    <div className="mt-4 h-6 w-full rounded bg-slate-200" />
                    <div className="mt-2 h-6 w-3/4 rounded bg-slate-200" />

                    <div className="mt-5 h-4 w-full rounded bg-slate-200" />
                    <div className="mt-2 h-4 w-5/6 rounded bg-slate-200" />
                    <div className="mt-2 h-4 w-2/3 rounded bg-slate-200" />

                    <div className="mt-8 h-4 w-24 rounded bg-slate-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : rest.length === 0 && !featured ? (
            <div className="rounded-2xl border border-dashed border-border bg-surface p-10 text-center">
              <p className="text-sm text-ink-soft">
                No articles match your filters yet. Try clearing the search or another category.
              </p>
            </div>
          ) : null}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
  {rest.map((p, i) => {
    const category =
      p._embedded?.["wp:term"]?.[0]?.[0]?.name || "General";

    const v = getCategoryVisual(category);

    const image =
      p._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
      "";

    return (
      <motion.div
        key={p.slug}
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease, delay: i * 0.06 }}
        whileHover={{ y: -4 }}
      >
        <Link
          to={`/blog/${p.slug}`}
          className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-surface-elevated transition duration-500 hover:border-ink/20 hover:shadow-soft"
        >
          {/* Image Section */}
          <div
            className={`relative aspect-[16/10] overflow-hidden bg-gradient-to-br ${v.gradient}`}
          >
            {/* Glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-10 -right-10 h-44 w-44 rounded-full blur-3xl"
              style={{ background: v.accent, opacity: 0.4 }}
            />

            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-12 -left-8 h-40 w-40 rounded-full bg-white/40 blur-3xl"
            />

            {/* Image */}
            <div className="absolute inset-0">
              {image ? (
                <img
                  src={image}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  alt={p.title.rendered}
                />
              ) : (
                <div className="h-full w-full bg-slate-100" />
              )}
            </div>

            {/* Category */}
            <span className="absolute left-4 top-4 rounded-full bg-background/90 px-2.5 py-1 text-[10.5px] font-medium uppercase tracking-wider text-ink backdrop-blur">
              {category}
            </span>
          </div>

          {/* Content */}
          <div className="flex flex-1 flex-col p-6">
            {/* Meta */}
            <div className="flex items-center gap-3 text-[11.5px] text-ink-soft">
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(p.date)}
              </span>

              <span>·</span>

              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {getReadMinutes(p.content?.rendered || "")} min read
              </span>
            </div>

            {/* Title */}
            <h3 className="mt-3 font-display text-[20px] leading-snug tracking-tight text-ink">
              {p.title.rendered}
            </h3>

            {/* Excerpt */}
            <div
              className="mt-2 line-clamp-3 text-[13.5px] leading-relaxed text-ink-soft"
              dangerouslySetInnerHTML={{
                __html: p.excerpt?.rendered || "",
              }}
            />

            {/* CTA */}
            <div className="mt-auto pt-5 text-[13px] font-medium text-ink">
              <span className="inline-flex items-center gap-1">
                Read article
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  })}
</div>

          {hasMore && (
            <div className="mt-12 flex justify-center">
              <button
                onClick={() => setPage(prev => prev + 1)}
                disabled={loading}
                className="bg-black text-white px-8 py-4 rounded"
              >
                {loading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function BlogHero({
  query,
  setQuery,
  activeCat,
  setCategory,
  categories,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-[#F9F9F6] pt-32 pb-20 md:pt-40 md:pb-28"
    >


      <motion.div
        style={{ y, opacity }}
        className="container relative z-10 mx-auto max-w-6xl px-6"
      >
        <div className="mx-auto max-w-3xl text-center">
          {/* Eyebrow */}
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease }}
            className="inline-flex items-center gap-2 rounded-full border border-[#D8DDD9] bg-white px-4 py-1 text-[11px] uppercase tracking-[0.2em] text-[#8FA998]"
          >
            <Tag className="h-3 w-3" />
            Dr. Neha Agarwal
          </motion.span>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="mt-6 font-serif text-5xl leading-tight text-[#1E3F33] md:text-7xl"
          >
            Insights & Health
            <span className="italic text-[#8FA998]"> Updates</span>
          </motion.h1>

          {/* Sub */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-[#4A6359]"
          >
            Educational articles, treatment updates, awareness guides and
            expert insights on gynecologic cancers and women's health.
          </motion.p>

          {/* Search bar — prominent, centered */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.35 }}
            className="mx-auto mt-10 max-w-xl"
          >
            <div className="group relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft transition group-focus-within:text-brand" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles, topics, keywords…"
                aria-label="Search blog articles"
                className="w-full rounded-full border border-[#D8DDD9] bg-white py-4 pl-11 pr-5 text-sm text-[#1E3F33] shadow-sm outline-none transition focus:border-[#8FA998]"
              />
            </div>
          </motion.div>

          {/* Category filters — centered, clean */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.5 }}
            role="tablist"
            aria-label="Filter by category"
            className="mx-auto mt-6 flex flex-wrap justify-center gap-2"
          >
            {["All", ...categories].map((c) => {
              const active = activeCat === c;
              return (
                <button
                  key={c}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setCategory(c)}
                  className={`rounded-full border px-4 py-1.5 text-[12.5px] font-medium transition ${active
                    ? "border-[#1E3F33] bg-[#1E3F33] text-white"
                    : "border-[#D8DDD9] bg-white text-[#4A6359] hover:border-[#8FA998]"
                    }`}
                >
                  {c}
                </button>
              );
            })}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
function getReadMinutes(html) {
  const text = html.replace(/<[^>]+>/g, "");
  const words = text.trim().split(/\s+/).length;
  return Math.ceil(words / 200);
}
function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
function getCategoryVisual() {
  return {
    gradient: "from-slate-200 to-slate-100",
    accent: "#10b981"
  };
}
export default BlogIndex;